/**
 * User Profile Example Component
 * 
 * Demonstrates how to use the Microsoft Graph API integration
 * to fetch and display user information from Azure AD
 * 
 * This is an example component that you can use as a reference
 * for integrating Graph API into your application
 */

import React, { useEffect, useState } from 'react';
import { useGraphAPI } from '../hooks/useGraphAPI';
import { GraphUser } from '../services/graphClient';

interface UserProfileExampleProps {
  /**
   * Whether to automatically load the profile on mount
   */
  autoLoad?: boolean;
  
  /**
   * Custom CSS class for the container
   */
  className?: string;
}

/**
 * User Profile Example Component
 * 
 * Displays the current user's profile information from Microsoft Graph
 * 
 * @example
 * ```tsx
 * <UserProfileExample autoLoad={true} />
 * ```
 */
export const UserProfileExample: React.FC<UserProfileExampleProps> = ({
  autoLoad = true,
  className = ''
}) => {
  const {
    loading,
    error,
    currentUser,
    currentUserPhoto,
    currentUserManager,
    fetchCurrentUserProfile,
    fetchCurrentUserPhoto,
    fetchCurrentUserManager,
    clearError
  } = useGraphAPI();

  const [showManager, setShowManager] = useState(false);

  // Load user profile on mount if autoLoad is enabled
  useEffect(() => {
    if (autoLoad) {
      loadProfile();
    }
  }, [autoLoad]);

  /**
   * Load the current user's profile and photo
   */
  const loadProfile = async () => {
    try {
      await fetchCurrentUserProfile();
      await fetchCurrentUserPhoto();
    } catch (err) {
      console.error('Failed to load profile:', err);
    }
  };

  /**
   * Load the current user's manager information
   */
  const loadManager = async () => {
    try {
      await fetchCurrentUserManager();
      setShowManager(true);
    } catch (err) {
      console.error('Failed to load manager:', err);
    }
  };

  if (loading && !currentUser) {
    return (
      <div className={`flex items-center justify-center p-8 ${className}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading profile...</span>
      </div>
    );
  }

  if (error && !currentUser) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error Loading Profile</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>
            <button
              onClick={() => {
                clearError();
                loadProfile();
              }}
              className="mt-2 text-sm text-red-800 hover:text-red-900 underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white shadow rounded-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <h2 className="text-xl font-semibold text-white">User Profile</h2>
        <p className="text-blue-100 text-sm mt-1">Microsoft Graph API Integration Example</p>
      </div>

      {/* Profile Content */}
      <div className="p-6">
        {currentUser ? (
          <div className="space-y-6">
            {/* Profile Photo and Basic Info */}
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                {currentUserPhoto ? (
                  <img
                    src={currentUserPhoto}
                    alt={`${currentUser.displayName}'s profile`}
                    className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-2xl font-semibold">
                    {currentUser.displayName
                      ?.split(' ')
                      .map(n => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2) || '?'}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-2xl font-bold text-gray-900 truncate">
                  {currentUser.displayName}
                </h3>
                <p className="text-sm text-gray-500 truncate">{currentUser.mail}</p>
                
                {currentUser.jobTitle && (
                  <p className="mt-1 text-sm font-medium text-gray-700">
                    {currentUser.jobTitle}
                  </p>
                )}
                
                {currentUser.department && (
                  <p className="text-sm text-gray-500">{currentUser.department}</p>
                )}
              </div>
            </div>

            {/* Additional Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentUser.officeLocation && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Office Location
                  </p>
                  <p className="mt-1 text-sm text-gray-900">{currentUser.officeLocation}</p>
                </div>
              )}

              {currentUser.mobilePhone && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Mobile Phone
                  </p>
                  <p className="mt-1 text-sm text-gray-900">{currentUser.mobilePhone}</p>
                </div>
              )}

              {currentUser.businessPhones && currentUser.businessPhones.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Business Phone
                  </p>
                  <p className="mt-1 text-sm text-gray-900">{currentUser.businessPhones[0]}</p>
                </div>
              )}

              {currentUser.userPrincipalName && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    User Principal Name
                  </p>
                  <p className="mt-1 text-sm text-gray-900 truncate">
                    {currentUser.userPrincipalName}
                  </p>
                </div>
              )}
            </div>

            {/* Manager Section */}
            <div className="border-t pt-4">
              {!showManager ? (
                <button
                  onClick={loadManager}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg className="-ml-1 mr-2 h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Show Manager
                    </>
                  )}
                </button>
              ) : currentUserManager ? (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Reports To</h4>
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-blue-200 flex items-center justify-center text-blue-700 text-sm font-semibold">
                        {currentUserManager.displayName
                          ?.split(' ')
                          .map(n => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2) || '?'}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        {currentUserManager.displayName}
                      </p>
                      <p className="text-xs text-blue-700">{currentUserManager.mail}</p>
                      {currentUserManager.jobTitle && (
                        <p className="text-xs text-blue-600 mt-1">{currentUserManager.jobTitle}</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No manager information available</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="border-t pt-4 flex space-x-3">
              <button
                onClick={loadProfile}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh Profile
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No profile loaded</h3>
            <p className="mt-1 text-sm text-gray-500">Click the button below to load your profile</p>
            <div className="mt-6">
              <button
                onClick={loadProfile}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Load Profile
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Usage Note */}
      <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          <strong>Note:</strong> This is an example component demonstrating Microsoft Graph API integration.
          Customize it for your needs or use the <code className="bg-gray-200 px-1 rounded">useGraphAPI</code> hook
          directly in your components.
        </p>
      </div>
    </div>
  );
};

export default UserProfileExample;
